import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, firstValueFrom, fromEvent } from 'rxjs';
import { map, catchError, takeUntil, timeout } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { WebAuthnService } from './webauthn.service';
import { LoggerService } from './logger.service';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON
} from '@simplewebauthn/browser';
import {
  PasskeyRegistrationOptionsRequest,
  PasskeyRegistrationOptionsResponse,
  PasskeyRegistrationRequest,
  PasskeyRegistrationResponse,
  PasskeyAuthenticationOptionsRequest,
  PasskeyAuthenticationOptionsResponse,
  PasskeyAuthenticationRequest,
  PasskeyAuthenticationResponse,
  PasskeyCheckResponse,
  PasskeyRevalidationResponse,
  PasskeyCredential,
  PasskeyFailureCode,
  PasskeyOperationError,
  PasskeyOperationOptions,
  PasskeyPhase
} from '../models/passkey.model';

interface ActivePasskeyOperation {
  generation: number;
  attemptId: string;
  context: 'login' | 'revalidation' | 'registration';
  startedAt: number;
  deadlineAt: number;
  controller: AbortController;
  externalSignal?: AbortSignal;
  onExternalAbort?: () => void;
}

interface ActivePasskeyLookup {
  key: string;
  controller: AbortController;
  callers: Set<symbol>;
  settled: boolean;
  promise: Promise<boolean>;
}

/**
 * Servicio principal para gestionar Passkeys (WebAuthn/FIDO2).
 * 
 * Este servicio orquesta el flujo completo de registro, autenticación y
 * gestión de Passkeys, coordinando entre el backend y el WebAuthnService.
 * 
 * Responsabilidades:
 * - Registro de nuevas Passkeys (flujo completo de 3 pasos)
 * - Login con Passkey existente (flujo completo de 3 pasos)
 * - Re-validación periódica sin PIN
 * - Verificación de existencia de Passkeys
 * - Listado y eliminación de Passkeys
 * 
 * Arquitectura:
 * - Usa environment.backendUrl para configuración centralizada
 * - Integra con WebAuthnService para operaciones del navegador
 * - Usa LoggerService para logs controlados por ambiente
 * - Maneja errores y los propaga con contexto
 * 
 * @example
 * // Verificar si usuario tiene passkey
 * const hasPasskey = await this.passkeyService.hasPasskey('user@example.com', 1);
 * 
 * // Registrar nueva passkey
 * const result = await this.passkeyService.registerPasskey(
 *   'user@example.com',
 *   1,
 *   'iPhone 14 de Pedro'
 * );
 * 
 * // Login con passkey
 * const auth = await this.passkeyService.loginWithPasskey('user@example.com', 1);
 */
@Injectable({
  providedIn: 'root'
})
export class PasskeyService {
  private static readonly TOTAL_OPERATION_TIMEOUT_MS = 90000;
  private static readonly HTTP_PHASE_TIMEOUT_MS = 15000;
  private static readonly AUTHENTICATOR_PHASE_TIMEOUT_MS = 60000;
  
  /**
   * URL base del API de Passkeys.
   * Usa environment.backendUrl para ser configurable por ambiente.
   */
  private readonly API_URL = `${environment.backendUrl}/auth/passkey`;
  private activeOperation: ActivePasskeyOperation | null = null;
  private readonly activePasskeyLookups = new Map<string, ActivePasskeyLookup>();
  private nextOperationGeneration = 0;

  constructor(
    private http: HttpClient,
    private webAuthnService: WebAuthnService,
    private logger: LoggerService
  ) {
    this.logger.info('🔐 [PasskeyService] Inicializado con URL:', this.API_URL);
  }

  // ═══════════════════════════════════════════════════════════════════
  // REGISTRO DE PASSKEY
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Ejecuta el flujo completo de registro de una nueva Passkey.
   * 
   * Este es un proceso de 3 pasos:
   * 
   * PASO 1: Solicitar opciones al backend
   * - Envía email, partner y deviceName
   * - Backend genera un challenge único
   * - Backend retorna opciones de creación
   * 
   * PASO 2: Crear credencial con WebAuthn
   * - Invoca API nativo del navegador
   * - Usuario confirma con biométrico (Face ID, Touch ID, etc.)
   * - Navegador genera par de claves (pública/privada)
   * - Clave privada se guarda en el dispositivo (nunca sale)
   * - Retorna clave pública y metadata
   * 
   * PASO 3: Enviar credencial al backend
   * - Envía clave pública y metadata
   * - Backend verifica y almacena la credencial
   * - Retorna confirmación
   * 
   * @param email - Email del usuario
   * @param partner - ID del partner/organización
   * @param deviceName - Nombre opcional del dispositivo (ej: "iPhone 14 de Pedro")
   * @returns Promise con la respuesta de registro
   * @throws Error si algún paso falla
   * 
   * @example
   * try {
   *   const result = await this.passkeyService.registerPasskey(
   *     'pedro@ejemplo.com',
   *     1,
   *     'iPhone 14 de Pedro'
   *   );
   *   console.log('Passkey registrada:', result.credentialId);
   * } catch (error) {
   *   console.error('Error en registro:', error.message);
   * }
   */
  async registerPasskey(
    email: string,
    partner: number,
    deviceName?: string,
    operationOptions?: PasskeyOperationOptions
  ): Promise<PasskeyRegistrationResponse> {
    const operation = this.startOperation(operationOptions, 'options', 'registration');
    
    this.logger.group('═══════════════════════════════════════════════════════════');
    this.logger.log('🔐 [Passkey] Iniciando registro completo');
    this.logger.log('   → Email:', email);
    this.logger.log('   → Partner:', partner);
    this.logger.log('   → Device:', deviceName || 'Auto-detectado');
    this.logger.group('══��════════════════════════════════════════════════════════');

    try {
      // PASO 1: Obtener opciones del backend
      this.logger.log('📡 PASO 1: Obteniendo opciones del backend...');
      const optionsRequest: PasskeyRegistrationOptionsRequest = {
        email,
        partner,
        deviceName
      };
      
      const options = await this.requestWithDeadline(
        this.http.post<PasskeyRegistrationOptionsResponse>(
          `${this.API_URL}/register/options`,
          optionsRequest
        ),
        'options',
        operation
      );
      this.assertOperationIsCurrent(operation, 'options');
      
      this.logger.log('✅ Opciones recibidas del backend');
      this.logger.debug('   → Challenge:', options.challenge.substring(0, 10) + '...');

      // PASO 2: Crear credencial con WebAuthn
      this.logger.log('🔐 PASO 2: Creando credencial con WebAuthn...');
      const credential = await this.registerWithDeadline(
        options as unknown as PublicKeyCredentialCreationOptionsJSON,
        operation
      );
      
      this.logger.log('✅ Credencial creada exitosamente');

      // PASO 3: Enviar credencial al backend
      this.logger.log('📡 PASO 3: Enviando credencial al backend...');
      const registrationRequest: PasskeyRegistrationRequest = {
        credentialId: credential.id,
        clientDataJSON: credential.response.clientDataJSON,
        attestationObject: credential.response.attestationObject,
        transports: credential.response.transports || ['internal'],
        deviceName
      };
      
      const response = await this.requestWithDeadline(
        this.http.post<PasskeyRegistrationResponse>(
          `${this.API_URL}/register`,
          registrationRequest
        ),
        'verification',
        operation
      );
      this.assertOperationIsCurrent(operation, 'verification');
      if (response?.success !== true) {
        throw new PasskeyOperationError('INVALID_RESPONSE', 'verification', 'InvalidRegistrationResponse');
      }
      
      this.logger.log('✅ [Passkey] Registro completado exitosamente');
      this.logger.groupEnd();
      this.logger.groupEnd();
      this.recordOperation(operation, 'verification', 'success');
      
      return response;
      
    } catch (error: unknown) {
      this.logger.error('❌ [Passkey] Error en registro:', error);
      this.logger.groupEnd();
      this.logger.groupEnd();
      const typedError = this.toOperationError(error, 'options', operation);
      this.recordOperation(operation, typedError.phase, 'failure', error);
      throw typedError;
    } finally {
      this.finishOperation(operation);
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // LOGIN CON PASSKEY
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Ejecuta el flujo completo de login con Passkey.
   * 
   * Este es un proceso de 3 pasos:
   * 
   * PASO 1: Solicitar opciones al backend
   * - Envía email y partner
   * - Backend genera un challenge único
   * - Backend retorna lista de credenciales permitidas
   * 
   * PASO 2: Obtener firma con WebAuthn
   * - Invoca API nativo del navegador
   * - Usuario confirma con biométrico
   * - Navegador firma el challenge con la clave privada
   * - Retorna firma criptográfica
   * 
   * PASO 3: Enviar firma al backend
   * - Envía firma y metadata
   * - Backend verifica la firma con la clave pública almacenada
   * - Si es válida, genera JWT y refreshToken
   * - Retorna tokens (igual que login tradicional)
   * 
   * @param email - Email del usuario
   * @param partner - ID del partner/organización
   * @returns Promise con JWT y refreshToken
   * @throws Error si algún paso falla
   * 
   * @example
   * try {
   *   const auth = await this.passkeyService.loginWithPasskey(
   *     'pedro@ejemplo.com',
   *     1
   *   );
   *   // El caller centraliza la sesión en memoria
   *   this.securityService.storeSession(auth);
   * } catch (error) {
   *   console.error('Error en login:', error.message);
   * }
   */
  async loginWithPasskey(
    email: string,
    partner: number,
    operationOptions?: PasskeyOperationOptions
  ): Promise<PasskeyAuthenticationResponse> {
    const operation = this.startOperation(operationOptions, 'options', 'login');
    
    this.logger.group('══════════════════��════════════════════════════════════════');
    this.logger.log('🔐 [Passkey] Iniciando login completo');
    this.logger.group('═══════════════════════════════════════════════════════════');

    try {
      // PASO 1: Obtener opciones del backend
      this.logger.log('📡 PASO 1: Obteniendo opciones del backend...');
      const optionsRequest: PasskeyAuthenticationOptionsRequest = {
        email,
        partner
      };
      
      const options = await this.requestWithDeadline(
        this.http.post<PasskeyAuthenticationOptionsResponse>(
          `${this.API_URL}/login/options`,
          optionsRequest
        ),
        'options',
        operation
      );
      this.assertOperationIsCurrent(operation, 'options');
      
      this.logger.log('✅ Opciones recibidas del backend');

      // PASO 2: Obtener firma con WebAuthn
      this.logger.log('🔐 PASO 2: Obteniendo firma con WebAuthn...');
      const assertion = await this.authenticateWithDeadline(
        options as unknown as PublicKeyCredentialRequestOptionsJSON,
        operation
      );
      
      this.logger.log('✅ Firma obtenida exitosamente');

      // PASO 3: Enviar firma al backend
      this.logger.log('📡 PASO 3: Enviando firma al backend...');
      const authRequest: PasskeyAuthenticationRequest = {
        credentialId: assertion.id,
        clientDataJSON: assertion.response.clientDataJSON,
        authenticatorData: assertion.response.authenticatorData,
        signature: assertion.response.signature
      };
      
      const response = await this.requestWithDeadline(
        this.http.post<PasskeyAuthenticationResponse>(
          `${this.API_URL}/login`,
          authRequest,
          { withCredentials: true }
        ),
        'verification',
        operation
      );
      this.assertOperationIsCurrent(operation, 'verification');
      this.assertValidLoginResponse(response);
      
      this.logger.log('✅ [Passkey] Login completado exitosamente');
      this.recordOperation(operation, 'verification', 'success');
      this.logger.groupEnd();
      this.logger.groupEnd();
      
      return response;
      
    } catch (error: any) {
      this.recordOperation(operation, this.toOperationError(error, 'options', operation).phase, 'failure', error);
      this.logger.groupEnd();
      this.logger.groupEnd();
      throw this.toOperationError(error, 'options', operation);
    } finally {
      this.finishOperation(operation);
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // RE-VALIDACIÓN
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Ejecuta el flujo completo de re-validación con Passkey.
   * 
   * La re-validación se usa cuando:
   * - El token JWT está próximo a expirar
   * - Se detecta inactividad prolongada
   * - Se requiere confirmar identidad para operación sensible
   * 
   * Ventaja sobre PIN por email:
   * - No requiere acceso al correo
   * - Más rápido (1-2 segundos vs minutos)
   * - Más seguro (biométrico vs código temporal)
   * - Mejor UX (sin interrumpir el flujo)
   * 
   * Flujo similar al login pero:
   * - Requiere JWT válido en el header
   * - No retorna nuevo JWT (solo confirma identidad)
   * - Backend actualiza timestamp de última validación
   * 
   * @returns Promise<boolean> - true si la re-validación fue exitosa
   * 
   * @example
   * // Antes de operación sensible
   * const isValid = await this.passkeyService.revalidateWithPasskey();
   * if (isValid) {
   *   // Proceder con operación
   * } else {
   *   // Mostrar error o solicitar PIN
   * }
   */
  async revalidateWithPasskey(operationOptions?: PasskeyOperationOptions): Promise<boolean> {
    const operation = this.startOperation(operationOptions, 'options', 'revalidation');
    
    this.logger.group('��══════════════════════════════════════════════════════════');
    this.logger.log('🔐 [Passkey] Iniciando re-validación');
    this.logger.group('═══════════════════════════════════════════════════════════');

    try {
      // PASO 1: Obtener opciones
      this.logger.log('📡 PASO 1: Obteniendo opciones del backend...');
      this.logger.log('   → El backend extraerá el email del JWT automáticamente');
      
      // El backend obtiene el email del JWT en el header Authorization
      // No es necesario enviarlo en el body
      const options = await this.requestWithDeadline(
        this.http.post<PasskeyAuthenticationOptionsResponse>(
          `${this.API_URL}/revalidate/options`,
          {} // Body vacío - el backend usa el JWT del header
        ),
        'options',
        operation
      );
      this.assertOperationIsCurrent(operation, 'options');
      
      this.logger.log('✅ Opciones recibidas');

      // PASO 2: Obtener firma
      this.logger.log('🔐 PASO 2: Obteniendo firma...');
      const assertion = await this.authenticateWithDeadline(
        options as unknown as PublicKeyCredentialRequestOptionsJSON,
        operation
      );
      
      this.logger.log('✅ Firma obtenida');

      // PASO 3: Enviar firma
      this.logger.log('📡 PASO 3: Enviando firma...');
      const authRequest: PasskeyAuthenticationRequest = {
        credentialId: assertion.id,
        clientDataJSON: assertion.response.clientDataJSON,
        authenticatorData: assertion.response.authenticatorData,
        signature: assertion.response.signature
      };
      
      const response = await this.requestWithDeadline(
        this.http.post<PasskeyRevalidationResponse>(
          `${this.API_URL}/revalidate`,
          authRequest
        ),
        'verification',
        operation
      );
      this.assertOperationIsCurrent(operation, 'verification');
      if (response?.success !== true) {
        throw this.createOperationError('AUTH_REJECTED', 'verification', 'InvalidRevalidationResponse');
      }
      
      this.logger.log('✅ [Passkey] Re-validación exitosa');
      this.recordOperation(operation, 'verification', 'success');
      this.logger.groupEnd();
      this.logger.groupEnd();
      
      return true;
      
    } catch (error: any) {
      this.recordOperation(operation, this.toOperationError(error, 'options', operation).phase, 'failure', error);
      this.logger.groupEnd();
      this.logger.groupEnd();
      throw this.toOperationError(error, 'options', operation);
    } finally {
      this.finishOperation(operation);
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // GESTIÓN DE PASSKEYS
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Verifica si un usuario tiene Passkeys registradas.
   * 
   * Se usa para:
   * - Mostrar/ocultar botón de login con passkey
   * - Decidir si ofrecer registro de passkey
   * - Determinar método de re-validación (passkey vs PIN)
   * 
   * Si no se proporcionan parámetros, usa el usuario actual
   * (requiere JWT en el header).
   * 
   * @param email - Email del usuario (opcional)
   * @param partner - ID del partner (opcional)
   * @returns Promise<boolean> - true si tiene al menos una passkey
   * 
   * @example
   * // Verificar usuario específico
   * const hasPasskey = await this.passkeyService.hasPasskey('user@example.com', 1);
   * 
   * // Verificar usuario actual (con JWT)
   * const hasPasskey = await this.passkeyService.hasPasskey();
   */
  async hasPasskey(
    email?: string,
    partner?: number,
    operationOptions?: PasskeyOperationOptions
  ): Promise<boolean> {
    if (operationOptions?.signal?.aborted) {
      throw this.createOperationError('CANCELLED', 'check', 'AbortError');
    }

    const lookupKey = this.createPasskeyLookupKey(email, partner);
    const lookup = this.activePasskeyLookups.get(lookupKey)
      ?? this.createPasskeyLookup(lookupKey, email, partner);
    const hasPasskey = await this.waitForPasskeyLookup(lookup, operationOptions);
    this.logger.info('🔍 [Passkey] Usuario tiene passkey:', hasPasskey);
    return hasPasskey;
  }

  private createPasskeyLookupKey(email: string | undefined, partner: number | undefined): string {
    const isSessionLookup = !email;
    const normalizedEmail = email?.trim().toLowerCase() ?? '';
    const normalizedPartner = partner ?? '';
    return `${isSessionLookup ? 'session' : 'anonymous'}|${normalizedEmail}|${normalizedPartner}`;
  }

  private createPasskeyLookup(
    key: string,
    email: string | undefined,
    partner: number | undefined
  ): ActivePasskeyLookup {
    const lookup: ActivePasskeyLookup = {
      key,
      controller: new AbortController(),
      callers: new Set<symbol>(),
      settled: false,
      promise: Promise.resolve(false)
    };
    this.activePasskeyLookups.set(key, lookup);

    let params = new HttpParams();
    if (email) params = params.set('email', email);
    if (partner !== undefined && partner !== null) params = params.set('partner', partner.toString());
    const options = email
      ? { params, headers: { 'X-Skip-Interceptor': 'true' } }
      : { params };

    lookup.promise = firstValueFrom(
      this.http.get<PasskeyCheckResponse>(`${this.API_URL}/check`, options).pipe(
        timeout({ first: PasskeyService.HTTP_PHASE_TIMEOUT_MS }),
        takeUntil(fromEvent(lookup.controller.signal, 'abort'))
      )
    ).then(response => {
      if (typeof response?.hasPasskey !== 'boolean') {
        throw this.createOperationError('INVALID_RESPONSE', 'check', 'InvalidPasskeyCheckResponse');
      }
      return response.hasPasskey;
    }).catch(error => {
      throw this.toLookupOperationError(error, lookup.controller.signal);
    }).finally(() => {
      lookup.settled = true;
      if (this.activePasskeyLookups.get(key) === lookup) {
        this.activePasskeyLookups.delete(key);
      }
    });

    return lookup;
  }

  private waitForPasskeyLookup(
    lookup: ActivePasskeyLookup,
    operationOptions: PasskeyOperationOptions | undefined
  ): Promise<boolean> {
    if (operationOptions?.signal?.aborted) {
      return Promise.reject(this.createOperationError('CANCELLED', 'check', 'AbortError'));
    }

    const caller = Symbol('passkey-lookup-caller');
    lookup.callers.add(caller);

    return new Promise<boolean>((resolve, reject) => {
      let completed = false;
      let deadlineTimer: ReturnType<typeof setTimeout> | undefined;
      const onAbort = () => settle(() => reject(this.createOperationError('CANCELLED', 'check', 'AbortError')));
      const cleanup = () => {
        operationOptions?.signal?.removeEventListener('abort', onAbort);
        if (deadlineTimer !== undefined) clearTimeout(deadlineTimer);
        lookup.callers.delete(caller);
        if (!lookup.settled && lookup.callers.size === 0) {
          lookup.controller.abort();
          if (this.activePasskeyLookups.get(lookup.key) === lookup) {
            this.activePasskeyLookups.delete(lookup.key);
          }
        }
      };
      const settle = (complete: () => void) => {
        if (completed) return;
        completed = true;
        cleanup();
        complete();
      };

      operationOptions?.signal?.addEventListener('abort', onAbort, { once: true });
      if (operationOptions?.deadlineAt !== undefined) {
        const remainingMs = operationOptions.deadlineAt - Date.now();
        if (remainingMs <= 0) {
          settle(() => reject(this.createOperationError('TIMEOUT', 'check', 'TimeoutError')));
          return;
        }
        deadlineTimer = setTimeout(
          () => settle(() => reject(this.createOperationError('TIMEOUT', 'check', 'TimeoutError'))),
          remainingMs
        );
      }

      lookup.promise.then(
        result => settle(() => resolve(result)),
        error => settle(() => reject(error))
      );
    });
  }

  private toLookupOperationError(error: unknown, signal: AbortSignal): PasskeyOperationError {
    if (error instanceof PasskeyOperationError) return error;
    if (signal.aborted) return this.createOperationError('CANCELLED', 'check', 'AbortError', undefined, error);
    const status = (error as { status?: number })?.status;
    const name = (error as { name?: string })?.name ?? '';
    if (name.includes('Timeout')) return this.createOperationError('TIMEOUT', 'check', name, status, error);
    if (status === 401 || status === 403) return this.createOperationError('AUTH_REJECTED', 'check', name, status, error);
    if (typeof status === 'number' && status >= 500) return this.createOperationError('SERVER', 'check', name, status, error);
    if (status === 0) return this.createOperationError('NETWORK', 'check', name, status, error);
    return this.createOperationError('UNKNOWN', 'check', name, status, error);
  }

  private startOperation(
    operationOptions: PasskeyOperationOptions | undefined,
    phase: PasskeyPhase,
    context: ActivePasskeyOperation['context']
  ): ActivePasskeyOperation {
    if (operationOptions?.signal?.aborted) {
      throw this.createOperationError('CANCELLED', phase, 'AbortError');
    }
    if (this.activeOperation) {
      throw this.createOperationError('BUSY', phase, 'PasskeyOperationInProgress');
    }

    const now = Date.now();
    const maximumDeadlineAt = now + PasskeyService.TOTAL_OPERATION_TIMEOUT_MS;
    const requestedDeadlineAt = operationOptions?.deadlineAt ?? maximumDeadlineAt;
    const operation: ActivePasskeyOperation = {
      generation: ++this.nextOperationGeneration,
      attemptId: this.createAttemptId(),
      context,
      startedAt: now,
      deadlineAt: Math.min(requestedDeadlineAt, maximumDeadlineAt),
      controller: new AbortController(),
      externalSignal: operationOptions?.signal
    };
    operation.onExternalAbort = () => operation.controller.abort();
    operation.externalSignal?.addEventListener('abort', operation.onExternalAbort, { once: true });
    if (operation.externalSignal?.aborted) {
      operation.controller.abort();
    }
    this.activeOperation = operation;
    this.assertOperationIsCurrent(operation, phase);
    return operation;
  }

  private finishOperation(operation: ActivePasskeyOperation): void {
    operation.externalSignal?.removeEventListener('abort', operation.onExternalAbort!);
    if (this.activeOperation?.generation === operation.generation) {
      this.activeOperation = null;
    }
  }

  private recordOperation(
    operation: ActivePasskeyOperation,
    phase: PasskeyPhase,
    result: 'success' | 'failure',
    error?: unknown
  ): void {
    const status = (error as { status?: number } | undefined)?.status;
    const event = {
      attemptId: operation.attemptId,
      portal: 'onboarding',
      context: operation.context,
      phase,
      durationMs: Date.now() - operation.startedAt,
      result,
      ...(typeof status === 'number' ? { status } : {})
    };
    this.logger.info('[PASSKEY_OPERATION]', event);
  }

  private createAttemptId(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }

  private async requestWithDeadline<T>(
    request: Observable<T>,
    phase: PasskeyPhase,
    operation: ActivePasskeyOperation
  ): Promise<T> {
    this.assertOperationIsCurrent(operation, phase);
    try {
      const response = await firstValueFrom(request.pipe(
        timeout({ first: this.remainingBudget(operation, phase, PasskeyService.HTTP_PHASE_TIMEOUT_MS) }),
        takeUntil(fromEvent(operation.controller.signal, 'abort'))
      ));
      this.assertOperationIsCurrent(operation, phase);
      return response;
    } catch (error) {
      throw this.toOperationError(error, phase, operation);
    }
  }

  private async authenticateWithDeadline(
    options: PublicKeyCredentialRequestOptionsJSON,
    operation: ActivePasskeyOperation
  ) {
    return this.performAuthenticatorWithDeadline(
      operation,
      () => this.webAuthnService.authenticateWithPasskey(options)
    );
  }

  private async registerWithDeadline(
    options: PublicKeyCredentialCreationOptionsJSON,
    operation: ActivePasskeyOperation
  ) {
    return this.performAuthenticatorWithDeadline(
      operation,
      () => this.webAuthnService.registerPasskey(options)
    );
  }

  private async performAuthenticatorWithDeadline<T>(
    operation: ActivePasskeyOperation,
    authenticate: () => Promise<T>
  ): Promise<T> {
    const phase: PasskeyPhase = 'authenticator';
    this.assertOperationIsCurrent(operation, phase);
    const timeoutMs = this.remainingBudget(operation, phase, PasskeyService.AUTHENTICATOR_PHASE_TIMEOUT_MS);
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let onAbort: (() => void) | undefined;
    try {
      const aborted = new Promise<never>((_, reject) => {
        onAbort = () => {
          this.webAuthnService.cancelAuthentication();
          reject(this.createOperationError('CANCELLED', phase, 'AbortError'));
        };
        operation.controller.signal.addEventListener('abort', onAbort, { once: true });
      });
      const timedOut = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          this.webAuthnService.cancelAuthentication();
          reject(this.createOperationError('TIMEOUT', phase, 'TimeoutError'));
        }, timeoutMs);
      });
      const assertion = await Promise.race([
        authenticate(),
        aborted,
        timedOut
      ]);
      this.assertOperationIsCurrent(operation, phase);
      return assertion;
    } catch (error) {
      throw this.toOperationError(error, phase, operation);
    } finally {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
      if (onAbort) {
        operation.controller.signal.removeEventListener('abort', onAbort);
      }
    }
  }

  private remainingBudget(
    operation: ActivePasskeyOperation,
    phase: PasskeyPhase,
    phaseMaximumMs: number
  ): number {
    const remaining = operation.deadlineAt - Date.now();
    if (remaining <= 0) {
      throw this.createOperationError('TIMEOUT', phase, 'TimeoutError');
    }
    return Math.min(remaining, phaseMaximumMs);
  }

  private assertOperationIsCurrent(operation: ActivePasskeyOperation, phase: PasskeyPhase): void {
    if (operation.controller.signal.aborted || this.activeOperation?.generation !== operation.generation) {
      throw this.createOperationError('CANCELLED', phase, 'AbortError');
    }
  }

  private assertValidLoginResponse(response: PasskeyAuthenticationResponse): void {
    const responseWithMfa = response as PasskeyAuthenticationResponse & { mfaRequired?: boolean; mfa_required?: boolean };
    if (!response?.jwt || response.status === false || responseWithMfa.mfaRequired === true || responseWithMfa.mfa_required === true) {
      throw this.createOperationError('AUTH_REJECTED', 'verification', 'InvalidLoginResponse');
    }
  }

  private toOperationError(
    error: unknown,
    phase: PasskeyPhase,
    operation: ActivePasskeyOperation
  ): PasskeyOperationError {
    if (error instanceof PasskeyOperationError) {
      return error;
    }
    if (operation.controller.signal.aborted) {
      return this.createOperationError('CANCELLED', phase, 'AbortError', undefined, error);
    }
    const status = (error as { status?: number })?.status;
    const name = (error as { name?: string })?.name ?? '';
    if (name.includes('Timeout')) {
      return this.createOperationError('TIMEOUT', phase, name, status, error);
    }
    if (status === 401 || status === 403) {
      return this.createOperationError('AUTH_REJECTED', phase, name, status, error);
    }
    if (typeof status === 'number' && status >= 500) {
      return this.createOperationError('SERVER', phase, name, status, error);
    }
    if (status === 0) {
      return this.createOperationError('NETWORK', phase, name, status, error);
    }
    return this.createOperationError('UNKNOWN', phase, name, status, error);
  }

  private createOperationError(
    code: PasskeyFailureCode,
    phase: PasskeyPhase,
    originalName: string,
    status?: number,
    cause?: unknown
  ): PasskeyOperationError {
    return new PasskeyOperationError(code, phase, originalName, status, { cause });
  }

  /**
   * Lista todas las Passkeys del usuario actual.
   * 
   * Requiere JWT válido en el header.
   * 
   * Útil para:
   * - Mostrar lista de dispositivos registrados
   * - Permitir eliminar passkeys antiguas
   * - Mostrar última vez que se usó cada passkey
   * 
   * @returns Observable con array de PasskeyCredential
   * 
   * @example
   * this.passkeyService.listPasskeys().subscribe(passkeys => {
   *   console.log('Dispositivos registrados:', passkeys.length);
   *   passkeys.forEach(pk => {
   *     console.log(`- ${pk.deviceName} (último uso: ${pk.lastUsedAt})`);
   *   });
   * });
   */
  listPasskeys(): Observable<PasskeyCredential[]> {
    this.logger.log('📋 [PasskeyService] Solicitando lista de passkeys...');
    return this.http.get<any>(`${this.API_URL}/list`).pipe(
      map(response => {
        // El backend puede retornar la lista directamente o envuelta en un objeto
        const passkeys = Array.isArray(response) ? response : (response.result || response.data || []);
        this.logger.log('✅ [PasskeyService] Passkeys recibidas:', passkeys.length);
        return passkeys;
      }),
      catchError(error => {
        this.logger.error('❌ [PasskeyService] Error obteniendo lista de passkeys:', error);
        throw error;
      })
    );
  }

  /**
   * Elimina una Passkey específica.
   * 
   * Requiere JWT válido en el header.
   * 
   * Casos de uso:
   * - Usuario perdió el dispositivo
   * - Usuario cambió de dispositivo
   * - Limpieza de passkeys antiguas no utilizadas
   * 
   * Nota: Si el usuario elimina todas sus passkeys, volverá
   * a usar el flujo tradicional (email + password + PIN).
   * 
   * @param credentialId - ID de la credencial a eliminar
   * @returns Observable con la respuesta del backend
   * 
   * @example
   * this.passkeyService.deletePasskey('xyz789...').subscribe(
   *   () => console.log('Passkey eliminada'),
   *   error => console.error('Error eliminando passkey:', error)
   * );
   */
  deletePasskey(credentialId: string): Observable<any> {
    return this.http.delete(`${this.API_URL}/${credentialId}`);
  }
}
